/**
 * WebSocket service — a true singleton owned by the app shell.
 *
 * Fixes vs. the previous version (audit §6.9):
 *   * `connect()` is idempotent: a second call while a socket exists (or a
 *     connect is in flight for the same token) is a no-op instead of opening
 *     another socket.
 *   * The 30 s keep-alive ping interval is tracked and cleared on
 *     `disconnect()`, so navigating between pages no longer leaks timers.
 *   * `onConnect` / `onDisconnect` return unsubscribe functions and the
 *     callback lists are cleared on disconnect.
 *   * Reconnect uses exponential backoff (2 s → 30 s, ×1.5) via
 *     reconnecting-websocket; `connectionState` exposes a tri-state for the
 *     shell's live-status pill.
 */
import ReconnectingWebSocket from 'reconnecting-websocket';
import { getAuthToken } from './api';

const WS_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000')
  .replace(/^http/, 'ws');

const PING_INTERVAL_MS = 30_000;

type MessageHandler = (message: WebSocketMessage) => void;
type StateHandler = (state: ConnectionState) => void;

export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

export interface WebSocketMessage {
  type: string;
  driver_id?: string;
  driver_name?: string;
  payload?: Record<string, unknown>;
  timestamp: string;
  [key: string]: unknown;
}

class WebSocketService {
  private ws: ReconnectingWebSocket | null = null;
  private connectedToken: string | null = null;
  private pingTimer: number | null = null;
  private handlers: Map<string, Set<MessageHandler>> = new Map();
  private onConnectCallbacks = new Set<() => void>();
  private onDisconnectCallbacks = new Set<() => void>();
  private stateCallbacks = new Set<StateHandler>();
  private _state: ConnectionState = 'disconnected';
  private _lastMessageAt: number | null = null;

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get connectionState(): ConnectionState {
    return this._state;
  }

  get lastMessageAt(): number | null {
    return this._lastMessageAt;
  }

  private setState(next: ConnectionState) {
    if (this._state === next) return;
    this._state = next;
    this.stateCallbacks.forEach((cb) => { try { cb(next); } catch (e) { console.error(e); } });
  }

  /**
   * Open the dispatch hub socket. Safe to call repeatedly — if a socket for
   * the current token already exists this is a no-op. If the token changed
   * (re-login / impersonation) the old socket is closed first.
   */
  connect(): void {
    const token = getAuthToken();
    if (!token) {
      if (import.meta.env.DEV) console.warn('WebSocket: no auth token available');
      return;
    }
    if (this.ws && this.connectedToken === token) return;
    if (this.ws) this.teardownSocket();

    const wsUrl = `${WS_BASE}/api/v1/dispatch/ws?token=${encodeURIComponent(token)}`;
    this.connectedToken = token;
    this.setState('connecting');

    const ws = new ReconnectingWebSocket(wsUrl, [], {
      maxRetries: Infinity,
      reconnectionDelayGrowFactor: 1.5,
      maxReconnectionDelay: 30_000,
      minReconnectionDelay: 2_000,
      connectionTimeout: 10_000,
      debug: false,
    });
    this.ws = ws;

    ws.onopen = () => {
      if (import.meta.env.DEV) console.log('WebSocket connected to dispatch hub');
      this.setState('connected');
      this.onConnectCallbacks.forEach((cb) => { try { cb(); } catch (e) { console.error(e); } });
    };

    ws.onclose = () => {
      // reconnecting-websocket will retry on its own; report "connecting"
      // while it does so the UI can show a reconnect state rather than dead.
      this.setState(this.ws === ws ? 'connecting' : 'disconnected');
      this.onDisconnectCallbacks.forEach((cb) => { try { cb(); } catch (e) { console.error(e); } });
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this._lastMessageAt = Date.now();
        if (message.type === 'pong') return;
        this.handlers.get(message.type)?.forEach((h) => { try { h(message); } catch (e) { console.error(e); } });
        this.handlers.get('*')?.forEach((h) => { try { h(message); } catch (e) { console.error(e); } });
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onerror = (error) => {
      if (import.meta.env.DEV) console.error('WebSocket error:', error);
    };

    // Single keep-alive timer, cleared on disconnect.
    this.pingTimer = window.setInterval(() => {
      if (this.isConnected) {
        try { this.ws?.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
      }
    }, PING_INTERVAL_MS);
  }

  private teardownSocket(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = null;
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      try { ws.close(); } catch { /* ignore */ }
    }
    this.connectedToken = null;
  }

  /** Close the socket and clear timers + connection callbacks. */
  disconnect(): void {
    this.teardownSocket();
    this.setState('disconnected');
    this.onConnectCallbacks.clear();
    this.onDisconnectCallbacks.clear();
  }

  /** Subscribe to a message type ('*' for all). Returns an unsubscribe fn. */
  on(messageType: string, handler: MessageHandler): () => void {
    let set = this.handlers.get(messageType);
    if (!set) { set = new Set(); this.handlers.set(messageType, set); }
    set.add(handler);
    return () => this.off(messageType, handler);
  }

  off(messageType: string, handler: MessageHandler): void {
    this.handlers.get(messageType)?.delete(handler);
  }

  onConnect(callback: () => void): () => void {
    this.onConnectCallbacks.add(callback);
    return () => { this.onConnectCallbacks.delete(callback); };
  }

  onDisconnect(callback: () => void): () => void {
    this.onDisconnectCallbacks.add(callback);
    return () => { this.onDisconnectCallbacks.delete(callback); };
  }

  /** Subscribe to connection-state changes (fires immediately with current). */
  onStateChange(callback: StateHandler): () => void {
    this.stateCallbacks.add(callback);
    callback(this._state);
    return () => { this.stateCallbacks.delete(callback); };
  }
}

// Singleton
export const wsService = new WebSocketService();
